import { useQuery } from "@apollo/client";
import { TextFieldProps } from "@mui/material";
import { gql } from "generated-graphql";
import { FacilityPickerFragment } from "generated-graphql/graphql";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { ControllerFieldState } from "react-hook-form";

import { EpisodicBadge } from "./EpisodicBadge";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";

gql(`
  fragment FacilityPicker on Facility {
    id
    name
    streetAddress1
    state
    colocationReportingFacilityId
  }
`);

/**
 * Also used to hydrate a facility id into a name outside the picker (e.g. the
 * omnisearch filter pills), which is why it is exported. `search: "ids:a,b,c"`
 * looks facilities up by id and ignores ids that don't parse.
 */
export const FACILITIES_QUERY = gql(`
  query FacilitiesPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...FacilityPicker
      }
      count
    }
  }
`);

const WASTE_ENABLED_FACILITIES_QUERY = gql(`
  query WasteEnabledFacilitiesPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities: wasteEnabledFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...FacilityPicker
      }
      count
    }
  }
`);

/**
 * The waste list plus whether each facility has an episodic event running —
 * its own query rather than a field on the shared fragment, so only the
 * caller that asks for the annotation pays for it. `activeEpisodicEvent` is
 * batched per request (see the API's activeEpisodicEvent loader), so this is
 * one extra query for a page of options rather than one per option.
 */
const WASTE_ENABLED_FACILITIES_WITH_EPISODIC_QUERY = gql(`
  query WasteEnabledFacilitiesPickerWithEpisodic($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities: wasteEnabledFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...FacilityPicker
        activeEpisodicEvent {
          id
        }
      }
      count
    }
  }
`);

/**
 * Whether an option came back carrying a running episodic event.
 *
 * Reads the field by presence rather than by type, because only the query
 * above selects it: every other caller's options are plain
 * `FacilityPickerFragment`s, and this has to answer false for them without
 * either widening the shared type or asserting one.
 */
function hasActiveEpisodicEvent(option: object): boolean {
  return "activeEpisodicEvent" in option && option.activeEpisodicEvent != null;
}

type FacilityQueryVariant = "all" | "waste";

function resolveFacilityQuery(variant: FacilityQueryVariant, annotateEpisodicEvents: boolean) {
  if (variant !== "waste") {
    return FACILITIES_QUERY;
  }
  return annotateEpisodicEvents
    ? WASTE_ENABLED_FACILITIES_WITH_EPISODIC_QUERY
    : WASTE_ENABLED_FACILITIES_QUERY;
}

/** Value can be a full fragment, a string ID, an array containing a single ID, or null. */
type FacilityPickerValue = FacilityPickerFragment | string | string[] | null | undefined;

interface FacilityPickerProps extends ControllerFieldState {
  onChange: (facility: FacilityPickerFragment | null) => void;
  defaultSearchTerm?: string;
  disabledFacilitiesById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?: FacilityPickerValue;
  lazyLoad?: boolean;
  disabled?: boolean;
  textFieldProps?: TextFieldProps;
  showErrors?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  facilityQueryVariant?: FacilityQueryVariant;
  /**
   * Hide facilities that are themselves co-located (non-null
   * colocationReportingFacilityId). The server-side validator remains the
   * correctness backstop.
   */
  hideColocatedReporting?: boolean;
  /**
   * Mark facilities that have an episodic event running, on the options and on
   * the chosen value. Waste variant only — the annotation rides that query.
   *
   * Opt-in because it costs a query the other pickers have no use for, and
   * because the mark only means something where the reader can act on it: the
   * container form, which asks next whether this container joins the event.
   */
  annotateEpisodicEvents?: boolean;
}

function isFullFacilityFragment(v: unknown): v is FacilityPickerFragment {
  return v != null && typeof v === "object" && "id" in v && "name" in v;
}

/** Extract a facility ID from the flexible value type, or null if no ID. */
function extractId(value: FacilityPickerValue): string | null {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0] ?? null;
  if (typeof value === "object" && "id" in value) return value.id;
  return null;
}

export const FacilityPicker = forwardRef<HTMLDivElement, FacilityPickerProps>(
  function FacilityPicker(
    {
      onChange,
      defaultSearchTerm = "",
      disabledFacilitiesById = [],
      disabledOptionLabel = "",
      label,
      value,
      error,
      lazyLoad = true,
      disabled,
      textFieldProps,
      showErrors = true,
      onOpen,
      onClose,
      facilityQueryVariant = "all",
      hideColocatedReporting = false,
      annotateEpisodicEvents = false,
    },
    ref
  ) {
    const itemCache = useRef<Map<string, FacilityPickerFragment>>(new Map());
    const [resolvedValue, setResolvedValue] = useState<FacilityPickerFragment | null>(null);

    // If the value is already a full object, cache it and use it directly.
    // Otherwise, determine which ID needs hydration.
    const idToHydrate = useMemo(() => {
      if (!value) return null;
      if (isFullFacilityFragment(value)) {
        itemCache.current.set(value.id, value);
        return null;
      }
      const id = extractId(value);
      if (id && itemCache.current.has(id)) return null;
      return id;
    }, [value]);

    const query = resolveFacilityQuery(facilityQueryVariant, annotateEpisodicEvents);

    const { data: hydrationData, loading: hydrationLoading } = useQuery(query, {
      variables: {
        search: idToHydrate ? `ids:${idToHydrate}` : "",
        pageSize: 1,
        page: 0,
      },
      skip: !idToHydrate,
    });

    // Cache hydrated results.
    useEffect(() => {
      if (hydrationData) {
        const items = hydrationData.facilities?.items ?? [];
        items.forEach((item: FacilityPickerFragment) => {
          itemCache.current.set(item.id, item);
        });
      }
    }, [hydrationData]);

    // Resolve the current value from the cache whenever value or hydration changes.
    useEffect(() => {
      if (!value) {
        setResolvedValue(null);
        return;
      }
      if (isFullFacilityFragment(value)) {
        setResolvedValue(value);
        return;
      }
      const id = extractId(value);
      if (id) {
        const cached = itemCache.current.get(id);
        if (cached) {
          setResolvedValue(cached);
          return;
        }
      }
      // Don't clear to null while hydrating — keep previous value visible
      if (!hydrationLoading) {
        setResolvedValue(null);
      }
    }, [value, hydrationLoading, hydrationData]);

    return (
      <AsyncAutocomplete
        ref={ref}
        query={query}
        variables={{}}
        initialPageSize={25}
        additionalSearchString={defaultSearchTerm}
        getItems={(data) => {
          const searchResults = hideColocatedReporting
            ? data.facilities.items.filter(
                (item: FacilityPickerFragment) => !item.colocationReportingFacilityId
              )
            : data.facilities.items;
          // Ensure the resolved value is included in options so MUI can match it
          if (resolvedValue) {
            const ids = new Set(searchResults.map((item: FacilityPickerFragment) => item.id));
            if (!ids.has(resolvedValue.id)) {
              return [...searchResults, resolvedValue];
            }
          }
          return searchResults;
        }}
        getOptionLabel={(option: FacilityPickerFragment) => {
          const name = option.name ?? "";
          return option.streetAddress1 ? `${name} (${option.streetAddress1})` : name;
        }}
        placeholderLabel={label ?? "Facility"}
        noOptionsText="Search for a facility"
        onChange={(val: FacilityPickerFragment | null) => {
          if (val) {
            itemCache.current.set(val.id, val);
          }
          setResolvedValue(val);
          onChange(val);
        }}
        value={resolvedValue}
        error={error}
        disabled={disabled || (!!idToHydrate && hydrationLoading)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionDisabled={(option: FacilityPickerFragment) =>
          disabledFacilitiesById.includes(option?.id ?? "")
        }
        renderOption={(props, option: FacilityPickerFragment) => {
          const displayName = option.streetAddress1
            ? `${option.name} (${option.streetAddress1})`
            : option.name;
          return (
            <li {...props} key={option.id}>
              {displayName}
              {hasActiveEpisodicEvent(option) && (
                <>
                  &nbsp;
                  <EpisodicBadge density="compact" />
                </>
              )}
              {disabledOptionLabel && disabledFacilitiesById.includes(option?.id ?? "") && (
                <span>&nbsp;({disabledOptionLabel})</span>
              )}
            </li>
          );
        }}
        textFieldProps={{
          variant: "outlined",
          autoComplete: "off",
          inputProps: {
            autoComplete: "off",
            "data-lpignore": "true",
            "data-form-type": "other",
          },
          ...textFieldProps,
          // On the chosen value too, not just the options: once the dropdown
          // closes the mark is the only thing still saying this facility has
          // an event running. After the name rather than before it, matching
          // the options — the name is what the reader is scanning for.
          ...(resolvedValue && hasActiveEpisodicEvent(resolvedValue)
            ? {
                InputProps: {
                  ...textFieldProps?.InputProps,
                  endAdornment: (
                    <>
                      <EpisodicBadge density="compact" />
                      {textFieldProps?.InputProps?.endAdornment}
                    </>
                  ),
                },
              }
            : {}),
        }}
        lazyLoad={lazyLoad}
        hideErrors={!showErrors}
        loading={!!idToHydrate && hydrationLoading}
        onOpen={onOpen}
        onClose={onClose}
      />
    );
  }
);
